import {createImageBackgroundFromObject, type ImageBackground, type ImageBackgroundObject} from '@PosterWhiteboard/page/background/image-background.class';
import {createVideoBackgroundFromObject, type VideoBackground, type VideoBackgroundObject} from '@PosterWhiteboard/page/background/video-background.class';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {ColorBackgroundObject} from '@PosterWhiteboard/page/background/color-background.class';
import {ColorBackground, createColorBackgroundFromObject} from '@PosterWhiteboard/page/background/color-background.class';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {UpdateFromObjectOpts} from '@PosterWhiteboard/common.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {hexToRgba} from '@Utils/color.util';
import {DEFAULT_BACKGROUND_COLOR} from '@PosterWhiteboard/page/page.types';
import {v4 as uuidv4} from 'uuid';
import type {DeepPartial} from '@/global';
import {BackgroundTypeName, isColorBackground, isImageBackground, isVideoBackground} from '@PosterWhiteboard/page/background/background.types';
import type {BackgroundType} from '@PosterWhiteboard/page/background/background.types';

export type BackgroundObject = ColorBackgroundObject | ImageBackgroundObject | VideoBackgroundObject;

export interface PageBackgroundObject {
  details: BackgroundObject;
  backgrounds?: BackgroundObject[];
  activeBackgroundId?: string;
}

export class PageBackground extends CommonMethods {
  public page: Page;
  public backgroundsHashMap: Record<string, BackgroundType> = {};
  public backgroundOrder: string[] = [];
  public activeBackgroundId = '';
  #timeListenerBound: ((time: number) => void) | null = null;

  constructor(page: Page) {
    super();
    this.page = page;
    const defaultBg = new ColorBackground(page);
    this.backgroundsHashMap[defaultBg.uid] = defaultBg;
    this.backgroundOrder = [defaultBg.uid];
    this.activeBackgroundId = defaultBg.uid;
  }

  /**
   * Facade getter — returns the active background.
   * All 70+ UI components that read background.details continue to work unchanged.
   */
  public get details(): BackgroundType {
    return this.getActiveBackground();
  }

  public getBackgrounds(): BackgroundType[] {
    return this.backgroundOrder.filter((uid) => this.backgroundsHashMap[uid] !== undefined).map((uid) => this.backgroundsHashMap[uid]);
  }

  public getActiveBackground(): BackgroundType {
    return this.backgroundsHashMap[this.activeBackgroundId] ?? this.backgroundsHashMap[this.backgroundOrder[0]];
  }

  public getBackgroundForTime(time: number): BackgroundType {
    const lastIndex = this.backgroundOrder.length - 1;
    for (let i = lastIndex; i >= 0; i--) {
      const bg = this.backgroundsHashMap[this.backgroundOrder[i]];
      if (i === lastIndex) {
        if (bg.onPageStartTime <= time) {
          return bg;
        }
      } else if (bg.onPageStartTime <= time && time < bg.onPageEndTime) {
        return bg;
      }
    }
    return this.backgroundsHashMap[this.backgroundOrder[0]];
  }

  public setActiveBackgroundId(id: string): void {
    this.activeBackgroundId = id;
  }

  public toObject(): PageBackgroundObject {
    const activeBg = this.getActiveBackground();
    const obj: PageBackgroundObject = {
      details: activeBg.toObject(),
    };

    if (this.backgroundOrder.length > 1) {
      obj.backgrounds = this.getBackgrounds().map((bg) => bg.toObject());
      obj.activeBackgroundId = this.activeBackgroundId;
    }

    return obj;
  }

  public toObjectWithNewUids(): PageBackgroundObject {
    const pageBackgroundObject = this.toObject();

    if (pageBackgroundObject.backgrounds) {
      const oldActiveId = pageBackgroundObject.activeBackgroundId;
      for (const bgObj of pageBackgroundObject.backgrounds) {
        const newUid = uuidv4();
        if (bgObj.uid === oldActiveId) {
          pageBackgroundObject.activeBackgroundId = newUid;
        }
        bgObj.uid = newUid;
      }
      pageBackgroundObject.details.uid = pageBackgroundObject.backgrounds[0].uid;
    } else {
      pageBackgroundObject.details.uid = uuidv4();
    }

    return pageBackgroundObject;
  }

  public async updateFromObject(
    pageBackgroundObject: DeepPartial<PageBackgroundObject>,
    {updateRedux = true, undoable = true, fireModifiedEvent = true, doInvalidate = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    if (pageBackgroundObject.backgrounds) {
      await this.#initAllBackgrounds(pageBackgroundObject.backgrounds);
      if (pageBackgroundObject.activeBackgroundId) {
        this.activeBackgroundId = pageBackgroundObject.activeBackgroundId;
      }

      if (doInvalidate) {
        this.invalidate();
      }
      if (undoable) {
        this.page.poster.history.addPosterHistory();
      }
      if (fireModifiedEvent) {
        this.page.fire('modified');
      }
      if (updateRedux) {
        this.page.poster.redux.updateReduxData();
      }
      return;
    }

    if (pageBackgroundObject.details) {
      await this.#updateActiveBackgroundFromObject(pageBackgroundObject.details, {updateRedux, undoable, fireModifiedEvent, doInvalidate});
    }
  }

  async #updateActiveBackgroundFromObject(
    detailsObject: DeepPartial<BackgroundObject>,
    {updateRedux = true, undoable = true, fireModifiedEvent = true, doInvalidate = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    const currentBackground = this.getActiveBackground();

    if (detailsObject.type !== undefined && currentBackground.type !== detailsObject.type) {
      await this.createNewBackgroundFromObject({details: detailsObject as BackgroundObject}, {updateRedux, undoable, fireModifiedEvent, doInvalidate});
      return;
    }

    if (isColorBackground(currentBackground)) {
      await currentBackground.updateFromObject(detailsObject as ColorBackgroundObject, {
        updateRedux: false,
        fireModifiedEvent: false,
        undoable: false,
        doInvalidate,
      });
    } else if (isImageBackground(currentBackground)) {
      if (this.#isImageBackgroundBeingChangedWithNewImage(currentBackground, detailsObject as ImageBackgroundObject)) {
        await this.createNewBackgroundFromObject({details: detailsObject as BackgroundObject}, {updateRedux, undoable, doInvalidate});
        return;
      }
      await currentBackground.updateFromObject(detailsObject as ImageBackgroundObject, {
        updateRedux: false,
        undoable: false,
        fireModifiedEvent: false,
        doInvalidate,
      });
    } else if (isVideoBackground(currentBackground)) {
      if (this.#isVideoBackgroundBeingChangedWithNewVideo(currentBackground, detailsObject as VideoBackgroundObject)) {
        await this.createNewBackgroundFromObject({details: detailsObject as BackgroundObject}, {updateRedux, undoable, doInvalidate});
        return;
      }
      await currentBackground.updateFromObject(detailsObject as VideoBackgroundObject, {
        updateRedux: false,
        undoable: false,
        fireModifiedEvent: false,
        doInvalidate,
      });
    } else {
      throw new Error('Unhandled background type');
    }

    if (doInvalidate) {
      this.invalidate();
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
  }

  #isImageBackgroundBeingChangedWithNewImage(currentBackground: ImageBackground, imageBackgroundObject: ImageBackgroundObject): boolean {
    return !!imageBackgroundObject.uid && imageBackgroundObject.uid !== currentBackground.uid;
  }

  #isVideoBackgroundBeingChangedWithNewVideo(currentBackground: VideoBackground, videoBackgroundObject: VideoBackgroundObject): boolean {
    return !!videoBackgroundObject.uid && videoBackgroundObject.uid !== currentBackground.uid;
  }

  public async createNewBackgroundFromObject(
    pageBackgroundObject: DeepPartial<PageBackgroundObject>,
    {updateRedux = true, fireModifiedEvent = true, undoable = true, doInvalidate = true}: UpdateFromObjectOpts = {}
  ): Promise<void> {
    const activeBg = this.getActiveBackground();
    const oldUid = activeBg.uid;
    activeBg.onRemove();

    const newBg = await this.#createBackgroundFromType(pageBackgroundObject.details as BackgroundObject);
    newBg.onPageStartTime = activeBg.onPageStartTime;
    newBg.onPageEndTime = activeBg.onPageEndTime;

    this.backgroundsHashMap[newBg.uid] = newBg;
    const index = this.backgroundOrder.indexOf(oldUid);
    this.backgroundOrder[index] = newBg.uid;
    this.backgroundsHashMap = Object.fromEntries(this.backgroundOrder.map((uid) => [uid, this.backgroundsHashMap[uid]]));
    this.activeBackgroundId = newBg.uid;

    await newBg.onBackgroundCreated();

    if (doInvalidate) {
      this.invalidate();
    }

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }
  }

  public isTransparent(): boolean {
    const bg = this.details;
    return (
      (isColorBackground(bg) && !bg.fill.hasFill()) ||
      (isImageBackground(bg) && bg.isBackgroundImageTransparencyOn())
    );
  }

  public isPremium(): boolean {
    return this.backgroundOrder.some(
      (uid) => {
        const bg = this.backgroundsHashMap[uid];
        return (isImageBackground(bg) || isVideoBackground(bg)) && bg.isPremium();
      }
    );
  }

  public invalidate(): void {
    const bg = this.details;
    if (this.isTransparent()) {
      this.page.fabricCanvas.backgroundColor = '#00000000';
    } else {
      if (isImageBackground(bg) || isVideoBackground(bg)) {
        this.page.fabricCanvas.backgroundColor = '#ffffff';
      }
    }
    this.page.fabricCanvas.renderAll();
  }

  public showWhiteBackgroundForTransparentBackground(): void {
    if (this.isTransparent()) {
      this.page.fabricCanvas.backgroundColor = '#ffffff';
    }
  }

  public showTransparentBackgroundForTransparentBackground(): void {
    if (this.isTransparent()) {
      this.page.fabricCanvas.backgroundColor = '#00000000';
    }
  }

  public hasImageBackground(): boolean {
    return isImageBackground(this.details);
  }

  public hasVideoBackground(): boolean {
    return isVideoBackground(this.details);
  }

  public hasAnyVideoBackground(): boolean {
    return this.backgroundOrder.some((uid) => isVideoBackground(this.backgroundsHashMap[uid]));
  }

  public updateBackgroundToDefaultState(): void {
    void this.updateFromObject(
      {
        details: {
          type: BackgroundTypeName.COLOR,
          fill: {
            fillType: FillTypes.SOLID,
            fillColor: [hexToRgba(DEFAULT_BACKGROUND_COLOR)],
          },
        },
      },
      {undoable: false}
    );
  }

  // ---- Playback delegation methods ----

  public async playActiveBackground(): Promise<void> {
    const bg = this.getActiveBackground();
    if (isVideoBackground(bg)) {
      await bg.play();
    }
  }

  public async pauseActiveBackground(): Promise<void> {
    const bg = this.getActiveBackground();
    if (isVideoBackground(bg)) {
      await bg.pause();
    }
  }

  public async stopAllBackgrounds(): Promise<void> {
    const promises: Promise<void>[] = [];
    for (const uid of this.backgroundOrder) {
      const bg = this.backgroundsHashMap[uid];
      if (isVideoBackground(bg)) {
        promises.push(bg.stop());
      }
    }
    await Promise.all(promises);
  }

  /**
   * Adjusts the last background's onPageEndTime to match the page duration.
   */
  public adjustLastBackgroundEndTime(pageDuration: number): void {
    if (this.backgroundOrder.length === 0) {
      return;
    }

    const lastBg = this.backgroundsHashMap[this.backgroundOrder[this.backgroundOrder.length - 1]];
    lastBg.onPageEndTime = pageDuration;
  }

  // ---- Time-based switching ----

  public initTimeListener(): void {
    if (this.#timeListenerBound) {
      return;
    }
    this.#timeListenerBound = this.#onTimeUpdated.bind(this);
    this.page.on('time:updated', this.#timeListenerBound);
  }

  public removeTimeListener(): void {
    if (this.#timeListenerBound) {
      this.page.off('time:updated', this.#timeListenerBound);
      this.#timeListenerBound = null;
    }
  }

  #onTimeUpdated(time: number): void {
    if (this.backgroundOrder.length <= 1) {
      return;
    }
    const bg = this.getBackgroundForTime(time);
    if (bg.uid !== this.activeBackgroundId) {
      void this.#switchCanvasToBackground(bg);
    }
  }

  async #switchCanvasToBackground(newBg: BackgroundType): Promise<void> {
    const oldBg = this.getActiveBackground();

    if (isVideoBackground(oldBg)) {
      await oldBg.pause();
    }

    this.activeBackgroundId = newBg.uid;

    if (isColorBackground(newBg)) {
      this.page.setBackgroundImage(undefined);
      await newBg.invalidate();
    } else if (isImageBackground(newBg)) {
      this.page.setBackgroundImage(newBg.fabricObject);
      this.page.fabricCanvas.backgroundColor = '#ffffff';
    } else if (isVideoBackground(newBg)) {
      this.page.setBackgroundImage(newBg.fabricObject);
      this.page.fabricCanvas.backgroundColor = '#ffffff';

      const pageTime = this.page.clock.getCurrentTime();
      await newBg.seek(pageTime - newBg.onPageStartTime);

      if (this.page.isPlaying) {
        await newBg.play();
      }
    }

    this.page.fabricCanvas.renderAll();
  }

  // ---- Background management API (for future UI) ----
  /**
   * This function doesn't trigger redux yet.
   */
  public async addBackground(backgroundObject: BackgroundObject): Promise<BackgroundType> {
    const bg = await this.#createBackgroundFromType(backgroundObject);

    const lastUid = this.backgroundOrder[this.backgroundOrder.length - 1];
    const lastBg = lastUid ? this.backgroundsHashMap[lastUid] : undefined;
    bg.onPageStartTime = lastBg ? lastBg.onPageEndTime : 0;
    bg.onPageEndTime = this.page.getDuration();

    this.backgroundsHashMap[bg.uid] = bg;
    this.backgroundOrder.push(bg.uid);

    return bg;
  }

  public removeBackground(backgroundUid: string): void {
    if (!this.backgroundsHashMap[backgroundUid] || this.backgroundOrder.length <= 1) {
      return;
    }

    this.backgroundsHashMap[backgroundUid].onRemove();
    this.backgroundOrder = this.backgroundOrder.filter((uid) => uid !== backgroundUid);
    this.backgroundsHashMap = Object.fromEntries(this.backgroundOrder.map((uid) => [uid, this.backgroundsHashMap[uid]]));

    if (this.activeBackgroundId === backgroundUid) {
      this.activeBackgroundId = this.backgroundOrder[0];
    }
  }

  public async splitBackground(backgroundUid: string, atTime: number): Promise<void> {
    const bg = this.backgroundsHashMap[backgroundUid];
    if (!bg || atTime <= bg.onPageStartTime || atTime >= bg.onPageEndTime) {
      return;
    }

    const {uid: _uid, ...newBgObject} = bg.toObject();
    const newBg = await this.#createBackgroundFromType(newBgObject as BackgroundObject);
    newBg.onPageStartTime = atTime;
    newBg.onPageEndTime = bg.onPageEndTime;

    bg.onPageEndTime = atTime;

    this.backgroundsHashMap[newBg.uid] = newBg;
    const index = this.backgroundOrder.indexOf(backgroundUid);
    this.backgroundOrder.splice(index + 1, 0, newBg.uid);
  }

  // ---- Internal helpers ----

  async #initAllBackgrounds(backgroundObjects: BackgroundObject[]): Promise<void> {
    for (const uid of this.backgroundOrder) {
      this.backgroundsHashMap[uid].onRemove();
    }

    const backgrounds = await Promise.all(
      backgroundObjects.map((bgObj) => this.#createBackgroundFromType(bgObj))
    );

    this.backgroundsHashMap = {};
    this.backgroundOrder = [];
    for (const bg of backgrounds) {
      this.backgroundsHashMap[bg.uid] = bg;
      this.backgroundOrder.push(bg.uid);
    }

    if (this.backgroundOrder.length > 0) {
      this.activeBackgroundId = this.backgroundOrder[0];
      await this.backgroundsHashMap[this.backgroundOrder[0]].onBackgroundCreated();

      const remainingVideoInits = this.backgroundOrder.slice(1)
        .map((uid) => this.backgroundsHashMap[uid])
        .filter(isVideoBackground)
        .map((bg) => bg.onItemAddedToPage());
      await Promise.all(remainingVideoInits);
    }
  }

  public disposeAllBackgrounds(): void {
    this.removeTimeListener();
    for (const uid of this.backgroundOrder) {
      this.backgroundsHashMap[uid].onRemove();
    }
  }

  async #createBackgroundFromType(backgroundObject: BackgroundObject): Promise<BackgroundType> {
    switch (backgroundObject.type) {
      case BackgroundTypeName.COLOR:
        return createColorBackgroundFromObject(this.page, backgroundObject);

      case BackgroundTypeName.IMAGE:
        return createImageBackgroundFromObject(this.page, backgroundObject);

      case BackgroundTypeName.VIDEO:
        return createVideoBackgroundFromObject(this.page, backgroundObject);

      default:
        throw new Error(`Couldn't load background. Unknown type`);
    }
  }
}
